import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Edit, Trophy, Clock, AlertCircle, RefreshCw } from 'lucide-react';

// Helper to get auth config for axios requests (mobile compatibility)
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

const DEFAULT_SCORING_RULES = [
  { event_name: 'Tribe or Team Wins Challenge', points: 3, notes: 'All members on winning tribe/team score' },
  { event_name: 'Individual Win', points: 5, notes: 'Immunity or reward' },
  { event_name: 'Individual or Team Challenge Runner-up', points: 2, notes: 'Runner-up or second place on Reward/Immunity' },
  { event_name: 'Finds an Idol or Advantage', points: 2, notes: 'Is the contestant to find an Idol or win/find an Advantage' },
  { event_name: 'Plays Idol/Advantage Successfully', points: 5, notes: 'Successfully only' },
  { event_name: 'Correct Shot in the Dark', points: 10, notes: 'Safe and used correctly' },
  { event_name: 'Vote Cast for Eliminated Player', points: 2, notes: 'Their vote matches the boot' },
  { event_name: 'Won Fire Making', points: 7, notes: 'Won fire making challenge at F4' },
  { event_name: 'Final Tribal Council Appearance', points: 15, notes: 'Reaches FTC' },
  { event_name: 'Wins Sole Survivor', points: 30, notes: 'Champion' },
  { event_name: 'Receives Votes but Survives', points: -2, notes: 'Survives the tribal where they got votes' },
  { event_name: 'Voted Out at Tribal Council', points: -5, notes: 'Eliminated from the game' },
  { event_name: 'Quits or Medically Evacuated', points: -10, notes: 'Leaves without a vote-out' },
];

export default function SuperAdminSettings() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    allow_new_registrations: true,
    allow_new_leagues: true,
    maintenance_mode: false,
    notifications_sandbox: false,
    maintenance_message: '',
    platform_timezone: 'America/Denver',
    default_bonus_opens_hours_before: 48,
    default_bonus_closes_hours_before: 0,
    default_scoring_rules: DEFAULT_SCORING_RULES
  });
  
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [editingRuleIndex, setEditingRuleIndex] = useState(null);
  const [ruleForm, setRuleForm] = useState({ event_name: '', points: '', notes: '' });

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadSettings();
  }, [user]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/platform/settings`, getAuthConfig());
      setSettings({
        ...response.data,
        default_scoring_rules: response.data.default_scoring_rules || DEFAULT_SCORING_RULES
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.patch(`${API}/platform/settings`, settings, getAuthConfig());
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const openAddRuleDialog = () => {
    setEditingRuleIndex(null);
    setRuleForm({ event_name: '', points: '', notes: '' });
    setShowRuleDialog(true);
  };

  const openEditRuleDialog = (index) => {
    setEditingRuleIndex(index);
    setRuleForm(settings.default_scoring_rules[index]);
    setShowRuleDialog(true);
  };

  const handleSaveRule = () => {
    if (!ruleForm.event_name || ruleForm.points === '') {
      toast.error('Event name and points are required');
      return;
    }

    const updatedRules = [...(settings.default_scoring_rules || [])];
    const rule = { ...ruleForm, points: parseInt(ruleForm.points) };

    if (editingRuleIndex !== null) {
      updatedRules[editingRuleIndex] = rule;
    } else {
      updatedRules.push(rule);
    }

    setSettings({ ...settings, default_scoring_rules: updatedRules });
    setShowRuleDialog(false);
    toast.success(editingRuleIndex !== null ? 'Rule updated' : 'Rule added');
  };

  const handleDeleteRule = (index) => {
    const updatedRules = settings.default_scoring_rules.filter((_, i) => i !== index);
    setSettings({ ...settings, default_scoring_rules: updatedRules });
    toast.success('Rule removed');
  };

  const handleResetToDefaults = async () => {
    if (!window.confirm('Reset all scoring rules to defaults?')) return;
    
    try {
      await axios.post(`${API}/platform/settings/reset-scoring-rules`, {}, getAuthConfig());
      await loadSettings();
      toast.success('Scoring rules reset to defaults');
    } catch (error) {
      console.error('Reset error:', error);
      toast.error('Failed to reset scoring rules');
    }
  };

  if (!user?.is_super_admin) return null;

  return (
    <SuperAdminLayout backLink="/super-admin">
      <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6">
        {/* Header - Title + Actions on same line */}
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="min-w-0">
            <h1 className="text-lg lg:text-2xl font-bold text-white">Settings</h1>
            <p className="text-xs lg:text-sm text-zinc-400">Platform Configuration</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 flex-shrink-0" size="sm">
            {saving ? <RefreshCw className="w-4 h-4 lg:mr-2 animate-spin" /> : <Save className="w-4 h-4 lg:mr-2" />}
            <span className="hidden lg:inline">{saving ? 'Saving...' : 'Save'}</span>
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-zinc-400">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading settings...
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {/* Access Control */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-white text-base sm:text-lg">Access Control</CardTitle>
                <CardDescription className="text-zinc-400 text-xs sm:text-sm">Control platform access</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0 pr-4">
                    <Label className="text-white text-sm">New Registrations</Label>
                    <p className="text-xs text-zinc-400">Allow new accounts</p>
                  </div>
                  <Switch
                    checked={settings.allow_new_registrations}
                    onCheckedChange={(checked) => setSettings({...settings, allow_new_registrations: checked})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0 pr-4">
                    <Label className="text-white text-sm">New Leagues</Label>
                    <p className="text-xs text-zinc-400">Allow league creation</p>
                  </div>
                  <Switch
                    checked={settings.allow_new_leagues}
                    onCheckedChange={(checked) => setSettings({...settings, allow_new_leagues: checked})}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Maintenance Mode */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-white text-base sm:text-lg">Maintenance Mode</CardTitle>
                <CardDescription className="text-zinc-400 text-xs sm:text-sm">Take platform offline</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0 pr-4">
                    <Label className="text-white text-sm">Enable Maintenance</Label>
                    <p className="text-xs text-zinc-400">Show maintenance message</p>
                  </div>
                  <Switch
                    checked={settings.maintenance_mode}
                    onCheckedChange={(checked) => setSettings({...settings, maintenance_mode: checked})}
                  />
                </div>
                {settings.maintenance_mode && (
                  <div>
                    <Label className="text-zinc-300 text-sm">Message</Label>
                    <Textarea
                      value={settings.maintenance_message || ''}
                      onChange={(e) => setSettings({...settings, maintenance_message: e.target.value})}
                      placeholder="We are performing scheduled maintenance..."
                      className="mt-2 bg-zinc-800 border-zinc-700 text-white text-sm"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notifications Sandbox */}
            <Card className="bg-zinc-900 border-amber-900/60">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-white text-base sm:text-lg">Notifications Sandbox</CardTitle>
                <CardDescription className="text-zinc-400 text-xs sm:text-sm">
                  Stops all real emails and texts — use this on Preview while testing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0 pr-4">
                    <Label className="text-white text-sm">Suppress all sending</Label>
                    <p className="text-xs text-zinc-400">
                      Messages still appear in the Comms logs marked "suppressed_sandbox", but nobody receives them
                    </p>
                  </div>
                  <Switch
                    data-testid="notifications-sandbox-switch"
                    checked={settings.notifications_sandbox || false}
                    onCheckedChange={(checked) => setSettings({...settings, notifications_sandbox: checked})}
                  />
                </div>
                {settings.notifications_sandbox && (
                  <p className="rounded-md border border-amber-700/50 bg-amber-950/40 px-3 py-2 text-xs text-amber-200">
                    Sandbox is ON. Real players will not receive anything until you turn it off. This setting lives in
                    this environment's database only — it never travels to Production on deploy.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Bonus Question Timing */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                  Bonus Question Timing
                </CardTitle>
                <CardDescription className="text-zinc-400 text-xs sm:text-sm">Default timing windows</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-zinc-300 text-sm">Platform Timezone</Label>
                  <Input
                    value={settings.platform_timezone}
                    onChange={(e) => setSettings({...settings, platform_timezone: e.target.value})}
                    className="mt-2 bg-zinc-800 border-zinc-700 text-white text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-300 text-xs sm:text-sm">Opens (hrs before)</Label>
                    <Input
                      type="number"
                      value={settings.default_bonus_opens_hours_before}
                      onChange={(e) => setSettings({...settings, default_bonus_opens_hours_before: parseInt(e.target.value)})}
                      className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300 text-xs sm:text-sm">Closes (hrs before)</Label>
                    <Input
                      type="number"
                      value={settings.default_bonus_closes_hours_before}
                      onChange={(e) => setSettings({...settings, default_bonus_closes_hours_before: parseInt(e.target.value)})}
                      className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Default Scoring Rules */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-2 sm:pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
                      <Trophy className="w-4 h-4 sm:w-5 sm:h-5" />
                      Default Scoring Rules
                    </CardTitle>
                    <CardDescription className="text-zinc-400 text-xs sm:text-sm">
                      Rules applied to new seasons
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleResetToDefaults} size="sm" variant="outline" className="text-amber-400 border-amber-400 hover:bg-amber-400/10 text-xs">
                      <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Reset
                    </Button>
                    <Button onClick={openAddRuleDialog} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs">
                      <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Add
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {settings.default_scoring_rules?.length === 0 ? (
                  <div className="text-center py-8 text-zinc-400">
                    <Trophy className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No default scoring rules</p>
                    <Button onClick={openAddRuleDialog} variant="outline" className="mt-3 text-zinc-300 text-sm">
                      <Plus className="w-4 h-4 mr-1" /> Add First Rule
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {settings.default_scoring_rules?.map((rule, index) => (
                      <div key={index} className="flex items-start justify-between p-2 sm:p-3 bg-zinc-800 rounded-lg gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                            <span className="font-medium text-white text-xs sm:text-sm">{rule.event_name}</span>
                            <span className={`font-bold text-xs ${rule.points >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {rule.points >= 0 ? '+' : ''}{rule.points}
                            </span>
                          </div>
                          {rule.notes && <p className="text-xs text-zinc-400 mt-0.5 line-clamp-1">{rule.notes}</p>}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openEditRuleDialog(index)}
                            className="text-zinc-400 hover:text-white h-7 w-7 p-0"
                          >
                            <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteRule(index)}
                            className="text-zinc-400 hover:text-red-400 h-7 w-7 p-0"
                          >
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-4 p-2 sm:p-3 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-zinc-400">
                      These defaults are copied to new seasons. League Admins can customize.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end pb-4">
              <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto">
                {saving ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Rule Dialog */}
      <Dialog open={showRuleDialog} onOpenChange={setShowRuleDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              {editingRuleIndex !== null ? 'Edit Rule' : 'Add Rule'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-zinc-300 text-sm">Event Name</Label>
              <Input
                value={ruleForm.event_name}
                onChange={(e) => setRuleForm({...ruleForm, event_name: e.target.value})}
                placeholder="Tribe Wins Challenge"
                className="mt-2 bg-zinc-800 border-zinc-700 text-white text-sm"
              />
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Points</Label>
              <Input
                type="number"
                value={ruleForm.points}
                onChange={(e) => setRuleForm({...ruleForm, points: e.target.value})}
                placeholder="5"
                className="mt-2 bg-zinc-800 border-zinc-700 text-white text-sm"
              />
              <p className="text-xs text-zinc-500 mt-1">Use negative for penalties</p>
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Notes (optional)</Label>
              <Textarea
                value={ruleForm.notes || ''}
                onChange={(e) => setRuleForm({...ruleForm, notes: e.target.value})}
                placeholder="When this rule applies..."
                className="mt-2 bg-zinc-800 border-zinc-700 text-white text-sm"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowRuleDialog(false)} className="text-zinc-300 w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={handleSaveRule} className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto">
              {editingRuleIndex !== null ? 'Update' : 'Add'} Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SuperAdminLayout>
  );
}
